import { useEffect, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"

import { golive } from "@/api/golive"
import { useSprintReport } from "@/api/sprint"
import { DeveloperWorkload } from "@/components/DeveloperWorkload"
import { ErrorBanner } from "@/components/ErrorBanner"
import { GoLive } from "@/components/GoLive"
import { LastUpdatedBadge } from "@/components/LastUpdatedBadge"
import { NewlyAdded } from "@/components/NewlyAdded"
import { P1Items, inP1P2Scope } from "@/components/P1Items"
import { PriorityMatrix } from "@/components/PriorityMatrix"
import { Releases } from "@/components/Releases"
import { ReportHeader } from "@/components/ReportHeader"
import { Roadmap } from "@/components/Roadmap"
import { SmokeTesting } from "@/components/SmokeTesting"
import { Standup } from "@/components/Standup"
import { StatusTrend } from "@/components/StatusTrend"
import { WeeklyDefects } from "@/components/WeeklyDefects"
import { WeeklyTrain } from "@/components/WeeklyTrain"
import { WorkedOn } from "@/components/WorkedOn"
import { aggregateMatrix } from "@/lib/matrix"
import type { Trend, TrendSeries, WorkItem, WorkItemType } from "@/types/sprint-report"

const TABS = [
  { id: "standup", label: "Standup" },
  { id: "main", label: "Main (Bugs)" },
  { id: "matrix", label: "Priority Matrix" },
  { id: "p1", label: "P1 / P2" },
  { id: "worked", label: "Worked On" },
  { id: "newly", label: "Newly Added" },
  { id: "trend", label: "Status Trend" },
  { id: "weekly", label: "Weekly Trend" },
  { id: "workload", label: "Developer Workload" },
  { id: "releases", label: "Releases" },
  { id: "roadmap", label: "10W Roadmap" },
  { id: "train", label: "Release Train" },
  { id: "smoketest", label: "Smoke Testing" },
  { id: "golive", label: "Go-Live" },
] as const

type TabId = (typeof TABS)[number]["id"]

const TYPE_OPTIONS: { type: WorkItemType; label: string }[] = [
  { type: "Bug", label: "Bug" },
  { type: "Product Backlog Item", label: "PBI" },
  { type: "Project Item", label: "Project Item" },
]
const ALL_TYPES = TYPE_OPTIONS.map((t) => t.type)
const TYPES_STORAGE_KEY = "catos-report-types"

/** Which sprint-filter chip an item falls under (its sprint number, else "Other"). */
function sprintKeyOf(it: WorkItem, nums: Set<number>): string {
  const n = it.sprint?.number
  return n != null && nums.has(n) ? `s${n}` : "other"
}

// Workflow progress rank (higher = closer to done). Used to order the standup
// so the most-advanced work floats up; Done sinks to the bottom.
const STATE_RANK: Record<string, number> = {
  new: 1,
  grooming: 2,
  "ux ready": 3,
  ready: 4,
  "dev ready": 5,
  committed: 6,
  "dev wip": 7,
  "dev pr": 8,
  "dev done": 9,
  "qa ready": 10,
  "qa wip": 11,
}
function progressKey(state: string, category?: string): number {
  const s = state.trim().toLowerCase()
  if (category === "done" || s === "done" || s === "closed") return -1 // → bottom
  return STATE_RANK[s] ?? 0
}

function loadTypes(): Set<WorkItemType> {
  try {
    const raw = localStorage.getItem(TYPES_STORAGE_KEY)
    if (raw) {
      const arr = JSON.parse(raw)
      if (Array.isArray(arr)) {
        const valid = arr.filter((t): t is WorkItemType => ALL_TYPES.includes(t))
        if (valid.length) return new Set(valid)
      }
    }
  } catch {
    /* ignore unreadable/blocked storage */
  }
  return new Set(ALL_TYPES)
}

/** Sum the per-type trend series for the selected types into one Trend. */
function sumTrendByTypes(trend: Trend, types: Set<WorkItemType>): Trend {
  if (!trend.by_type) return trend
  const series = [...types]
    .map((t) => trend.by_type?.[t])
    .filter((s): s is TrendSeries => Boolean(s))
  const { days } = trend
  return {
    days,
    by_type: trend.by_type,
    per_state: days.map((date, i) => {
      const by_state: Record<string, number> = {}
      for (const s of series) {
        for (const [k, v] of Object.entries(s.per_state[i]?.by_state ?? {})) {
          by_state[k] = (by_state[k] ?? 0) + v
        }
      }
      return { date, by_state }
    }),
    open_total: days.map((date, i) => ({
      date,
      count: series.reduce((sum, s) => sum + (s.open_total[i]?.count ?? 0), 0),
    })),
    flow: days.map((date, i) => ({
      date,
      inflow: series.reduce((sum, s) => sum + (s.flow[i]?.inflow ?? 0), 0),
      outflow: series.reduce((sum, s) => sum + (s.flow[i]?.outflow ?? 0), 0),
    })),
  }
}

export default function App() {
  const [tab, setTab] = useState<TabId>("standup")
  const [types, setTypes] = useState<Set<WorkItemType>>(loadTypes)
  // Standup-only assignee filter (empty = show all owners).
  const [standupAssignees, setStandupAssignees] = useState<Set<string>>(new Set())
  // P1/P2 sprint filter (empty = show all sprints).
  const [sprintFilter, setSprintFilter] = useState<Set<string>>(new Set())
  const { data, isLoading, isFetching, isError, error, dataUpdatedAt, refetch } =
    useSprintReport()

  // Who's viewing — an account may carry a tab allowlist (null = all tabs). The
  // query key matches SiteGate's, so this reads the already-cached result.
  const { data: who } = useQuery({
    queryKey: ["whoami"],
    queryFn: golive.whoami,
    staleTime: 5 * 60 * 1000,
  })
  const visibleTabs = useMemo(() => {
    const allow = who?.tabs
    if (!allow || !allow.length) return TABS
    const filtered = TABS.filter((t) => allow.includes(t.id))
    return filtered.length ? filtered : TABS // never leave an empty tab bar
  }, [who])
  // If the active tab isn't visible to this account, fall back to the first one.
  useEffect(() => {
    if (!visibleTabs.some((t) => t.id === tab)) setTab(visibleTabs[0].id)
  }, [visibleTabs, tab])

  // Sign out: clears the token (localStorage) + the httponly session cookie, then
  // reloads so the gate re-runs whoami and shows the login wall. Without this a
  // stale cookie would keep the previous person signed in (and their tabs).
  const signOut = async () => {
    try {
      await golive.logout()
    } catch {
      /* clear locally regardless */
    }
    window.location.reload()
  }

  useEffect(() => {
    try {
      localStorage.setItem(TYPES_STORAGE_KEY, JSON.stringify([...types]))
    } catch {
      /* ignore blocked storage */
    }
  }, [types])

  const filteredItems = useMemo(
    () => (data ? data.items.filter((i) => types.has(i.work_item_type)) : []),
    [data, types],
  )
  // Sprint-filter chips for the P1/P2 tab: previous, current, next + 1 future.
  const sprintChips = useMemo(() => {
    const s = data?.sprints
    const out: { key: string; label: string; num: number }[] = []
    const push = (sp: { number: number | null } | undefined, suffix = "") => {
      if (sp?.number != null) out.push({ key: `s${sp.number}`, label: `S${sp.number}${suffix}`, num: sp.number })
    }
    if (s) {
      push(s.previous)
      push(s.current, " (current)")
      push(s.next)
      push(s.future?.[0])
    }
    return out
  }, [data])
  const sprintNums = useMemo(() => new Set(sprintChips.map((c) => c.num)), [sprintChips])
  // Items shown on the P1/P2 tab, narrowed by the sprint filter (empty = all).
  const p1pItems = useMemo(() => {
    if (sprintFilter.size === 0) return filteredItems
    return filteredItems.filter((it) => sprintFilter.has(sprintKeyOf(it, sprintNums)))
  }, [filteredItems, sprintFilter, sprintNums])
  const matrix = useMemo(() => aggregateMatrix(filteredItems), [filteredItems])
  const matrixBugs = useMemo(
    () => aggregateMatrix(filteredItems.filter((i) => i.work_item_type === "Bug")),
    [filteredItems],
  )
  const workedItems = useMemo(() => {
    const base = data ? data.aggregations.worked_on ?? data.items.filter((i) => i.in_r1) : []
    return base.filter((i) => types.has(i.work_item_type))
  }, [data, types])
  const newlyItems = useMemo(
    () =>
      data ? (data.aggregations.newly_added ?? []).filter((i) => types.has(i.work_item_type)) : [],
    [data, types],
  )
  const filteredTrend = useMemo(
    () => (data?.aggregations.trend ? sumTrendByTypes(data.aggregations.trend, types) : undefined),
    [data, types],
  )
  // Standup: distinct owners (for the assignee filter) + groups filtered by the
  // selected owners and the global type filter (applied to work-item rows).
  const standupOwners = useMemo(() => {
    const names = new Set<string>()
    for (const g of data?.aggregations.standup?.groups ?? []) {
      names.add(g.owner?.name ?? "Unassigned")
    }
    return [...names].sort()
  }, [data])
  const standupGroups = useMemo(() => {
    const all = data?.aggregations.standup?.groups ?? []
    const groupKey = (g: (typeof all)[number]) =>
      g.kind === "bundle"
        ? progressKey(g.bundle?.state ?? "")
        : progressKey(g.item?.state ?? "", g.item?.state_category)
    return all
      .filter((g) => standupAssignees.size === 0 || standupAssignees.has(g.owner?.name ?? "Unassigned"))
      .map((g) => {
        if (g.kind === "item") {
          return g.item && types.has(g.item.work_item_type as WorkItemType) ? g : null
        }
        // member items: filter by type, then order by readiness (Done last)
        const items = (g.items ?? [])
          .filter((m) => types.has(m.work_item_type as WorkItemType))
          .sort((a, b) => progressKey(b.state, b.state_category) - progressKey(a.state, a.state_category))
        return { ...g, items }
      })
      .filter((g): g is NonNullable<typeof g> => g !== null)
      .sort((a, b) => groupKey(b) - groupKey(a))
  }, [data, standupAssignees, types])
  // Current-sprint bundles each person leads (Standup owner) — for the Developer
  // Workload "Bundles led" column. Counts bundle groups only, not standalone items.
  const bundlesLed = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const g of data?.aggregations.standup?.groups ?? []) {
      if (g.kind !== "bundle") continue
      const name = g.owner?.name?.trim() || "(unassigned)"
      counts[name] = (counts[name] ?? 0) + 1
    }
    return counts
  }, [data])
  // The dataset the active tab actually renders (before the type filter) — so the
  // chip counts reflect the current view, not the whole snapshot.
  const tabBaseItems = useMemo<WorkItem[]>(() => {
    if (!data) return []
    switch (tab) {
      case "worked":
        return data.aggregations.worked_on ?? data.items.filter((i) => i.in_r1)
      case "standup":
        return data.items.filter((i) => i.sprint?.is_current)
      case "newly":
        return (data.aggregations.newly_added ?? []).filter((i) => i.state_category !== "done")
      case "p1":
        return data.items.filter(inP1P2Scope)
      case "trend":
        // The trend is scoped to the R1 set (same as Worked On), so its chip
        // counts use the open R1 items.
        return data.aggregations.worked_on ?? data.items.filter((i) => i.in_r1)
      default:
        return data.items
    }
  }, [data, tab])
  const typeCounts = useMemo(() => {
    const counts: Partial<Record<WorkItemType, number>> = {}
    for (const i of tabBaseItems) counts[i.work_item_type] = (counts[i.work_item_type] ?? 0) + 1
    return counts
  }, [tabBaseItems])

  if (isLoading) {
    return (
      <main className="flex h-screen items-center justify-center text-slate-500">
        Loading sprint status…
      </main>
    )
  }

  if (isError || !data) {
    return (
      <main className="p-6">
        <ErrorBanner error={error} onRetry={() => refetch()} />
      </main>
    )
  }

  const toggleType = (t: WorkItemType) =>
    setTypes((prev) => {
      const next = new Set(prev)
      if (next.has(t)) next.delete(t)
      else next.add(t)
      return next
    })
  const toggleAssignee = (name: string) =>
    setStandupAssignees((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  const toggleSprint = (key: string) =>
    setSprintFilter((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  return (
    <main className="mx-auto max-w-screen-2xl p-4 print:p-0">
      <ReportHeader
        sprint={data.sprints.current}
        previousSprint={data.sprints.previous}
        nextSprint={data.sprints.next}
      />
      <LastUpdatedBadge at={dataUpdatedAt} refreshing={isFetching} />

      {who?.viewLocked && who.user && (
        <div className="mb-2 flex items-center justify-end gap-2 text-[11px] text-slate-400 print:hidden">
          <span>
            Signed in as <span className="font-semibold text-slate-600">{who.user}</span>
          </span>
          <button
            onClick={signOut}
            className="rounded-md px-2 py-0.5 font-semibold text-slate-500 ring-1 ring-slate-200 transition hover:bg-slate-100 hover:text-ink"
          >
            Sign out
          </button>
        </div>
      )}

      <nav className="mb-3 flex flex-wrap gap-1 rounded-xl border border-slate-200/70 bg-white/60 p-1 shadow-card backdrop-blur-sm print:hidden">
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={
              tab === t.id
                ? "rounded-lg bg-ink px-3 py-1.5 text-xs font-semibold text-white shadow-card transition"
                : "rounded-lg px-3 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-100 hover:text-ink"
            }
            aria-current={tab === t.id ? "page" : undefined}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {/* Work-item-type filter — applies to every tab except Weekly Trend,
          whose rows are fixed by definition (Status Trend uses the per-type
          breakdown). Selection persists across reloads. */}
      {tab !== "weekly" && tab !== "golive" && tab !== "releases" && tab !== "roadmap" && tab !== "train" && tab !== "smoketest" && (
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs print:hidden">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Types</span>
        {TYPE_OPTIONS.map(({ type, label }) => {
          const on = types.has(type)
          return (
            <button
              key={type}
              type="button"
              aria-pressed={on}
              onClick={() => toggleType(type)}
              className={
                on
                  ? "inline-flex items-center gap-1.5 rounded-full bg-headerSub/10 px-3 py-1 font-semibold text-header ring-1 ring-headerSub/30 transition"
                  : "inline-flex items-center gap-1.5 rounded-full bg-white/50 px-3 py-1 text-slate-400 ring-1 ring-slate-200 transition hover:text-slate-600"
              }
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${on ? "bg-headerSub" : "bg-slate-300"}`}
              />
              {label} <span className="num opacity-60">{typeCounts[type] ?? 0}</span>
            </button>
          )
        })}
      </div>
      )}

      {/* P1/P2-only sprint filter (empty = all sprints). */}
      {tab === "p1" && sprintChips.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5 text-xs print:hidden">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Sprint
          </span>
          {[...sprintChips, { key: "other", label: "Other", num: -1 }].map((c) => {
            const active = sprintFilter.size === 0 || sprintFilter.has(c.key)
            return (
              <button
                key={c.key}
                type="button"
                aria-pressed={sprintFilter.has(c.key)}
                onClick={() => toggleSprint(c.key)}
                className={
                  active
                    ? "rounded-full bg-indigo-50 px-2.5 py-0.5 font-medium text-indigo-700 ring-1 ring-indigo-200 transition"
                    : "rounded-full bg-white/50 px-2.5 py-0.5 text-slate-400 ring-1 ring-slate-200 transition hover:text-slate-600"
                }
              >
                {c.label}
              </button>
            )
          })}
          {sprintFilter.size > 0 && (
            <button
              type="button"
              onClick={() => setSprintFilter(new Set())}
              className="ml-1 text-[11px] text-slate-400 underline hover:text-slate-600"
            >
              clear
            </button>
          )}
        </div>
      )}

      {/* Standup-only assignee filter (by group owner; empty = all). */}
      {tab === "standup" && standupOwners.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5 text-xs print:hidden">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Owner</span>
          {standupOwners.map((name) => {
            const on = standupAssignees.size === 0 || standupAssignees.has(name)
            return (
              <button
                key={name}
                type="button"
                aria-pressed={standupAssignees.has(name)}
                onClick={() => toggleAssignee(name)}
                className={
                  on
                    ? "rounded-full bg-win-light px-2.5 py-0.5 font-medium text-win-deep ring-1 ring-win/30 transition"
                    : "rounded-full bg-white/50 px-2.5 py-0.5 text-slate-400 ring-1 ring-slate-200 transition hover:text-slate-600"
                }
              >
                {name}
              </button>
            )
          })}
          {standupAssignees.size > 0 && (
            <button
              type="button"
              onClick={() => setStandupAssignees(new Set())}
              className="ml-1 text-[11px] text-slate-400 underline hover:text-slate-600"
            >
              clear
            </button>
          )}
        </div>
      )}

      <div key={tab} className="panel animate-rise p-4 sm:p-5">
        {tab === "main" && <PriorityMatrix matrix={matrixBugs} />}
        {tab === "matrix" && <PriorityMatrix matrix={matrix} />}
        {tab === "p1" && <P1Items items={p1pItems} />}
        {tab === "worked" && <WorkedOn items={workedItems} />}
        {tab === "standup" && <Standup groups={standupGroups} />}
        {tab === "newly" && <NewlyAdded items={newlyItems} />}
        {tab === "trend" &&
          (filteredTrend ? (
            <StatusTrend trend={filteredTrend} />
          ) : (
            <p className="p-6 text-center text-sm text-slate-500">
              Trend data unavailable in this snapshot.
            </p>
          ))}
        {tab === "weekly" &&
          (data.aggregations.weekly_defects ? (
            <WeeklyDefects data={data.aggregations.weekly_defects} />
          ) : (
            <p className="p-6 text-center text-sm text-slate-500">
              Weekly defect trend unavailable in this snapshot — it appears after the next
              backend refresh (needs ADO history queries).
            </p>
          ))}
        {tab === "workload" && <DeveloperWorkload items={filteredItems} bundlesLed={bundlesLed} />}
        {tab === "releases" &&
          (data.aggregations.releases ? (
            <Releases data={data.aggregations.releases} />
          ) : (
            <p className="p-6 text-center text-sm text-slate-500">
              Release data unavailable in this snapshot — it appears after the next backend refresh.
            </p>
          ))}
        {tab === "roadmap" && <Roadmap releases={data.aggregations.releases} />}
        {tab === "train" && <WeeklyTrain />}
        {tab === "smoketest" && <SmokeTesting releases={data.aggregations.releases} />}
        {tab === "golive" && <GoLive />}
      </div>

      <footer className="num mt-4 text-[10px] text-slate-400">
        run_id: {data.metadata.run_id} · items: {data.metadata.items_included} ·
        scope: P0/P1/P2 (Bugs+PIs) + P1 PBIs · schema {data.schema_version}
      </footer>
    </main>
  )
}
